import { useEffect, useState } from 'react'
import { RefreshCw, TrendingUp, Heart, MessageCircle, ExternalLink, Hash } from 'lucide-react'

interface InstagramTrend {
  id: string
  hashtag: string
  posts: number
  engagement: number
  growth: string
  relatedTo: string[]
}

interface InstagramPost {
  id: string
  image: string
  caption: string
  likes: number
  comments: number
  date: string
  hashtags: string[]
}

export default function InstagramPage() {
  const [trends, setTrends] = useState<InstagramTrend[]>([])
  const [posts, setPosts] = useState<InstagramPost[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchInstagramData()
  }, [])

  const fetchInstagramData = async () => {
    setLoading(true)
    // TODO: Implementar chamada real à API do Instagram
    // Por enquanto, dados mockados baseados em trends relacionadas à marca
    setTimeout(() => {
      setTrends([
        {
          id: '1',
          hashtag: '#guiltybeauty',
          posts: 12500,
          engagement: 45000,
          growth: '+15%',
          relatedTo: ['beauty', 'makeup', 'skincare']
        },
        {
          id: '2',
          hashtag: '#bperfectcosmetics',
          posts: 850000,
          engagement: 3200000,
          growth: '+18%',
          relatedTo: ['makeup', 'palettes', 'bperfect']
        },
        {
          id: '3',
          hashtag: '#danessamyricks',
          posts: 420000,
          engagement: 1800000,
          growth: '+22%',
          relatedTo: ['makeup', 'foundation', 'danessa']
        },
        {
          id: '4',
          hashtag: '#kryolan',
          posts: 680000,
          engagement: 2500000,
          growth: '+12%',
          relatedTo: ['makeup', 'professional', 'kryolan']
        },
        {
          id: '5',
          hashtag: '#makeupatelier',
          posts: 320000,
          engagement: 1200000,
          growth: '+25%',
          relatedTo: ['makeup', 'atelier', 'paris']
        },
        {
          id: '6',
          hashtag: '#cerave',
          posts: 2500000,
          engagement: 9800000,
          growth: '+30%',
          relatedTo: ['skincare', 'dermatologist', 'cerave']
        },
        {
          id: '7',
          hashtag: '#bioderma',
          posts: 1800000,
          engagement: 7200000,
          growth: '+15%',
          relatedTo: ['skincare', 'dermatological', 'bioderma']
        },
        {
          id: '8',
          hashtag: '#isdin',
          posts: 950000,
          engagement: 3800000,
          growth: '+20%',
          relatedTo: ['skincare', 'sunscreen', 'isdin']
        },
        {
          id: '9',
          hashtag: '#foundation',
          posts: 4500000,
          engagement: 18000000,
          growth: '+10%',
          relatedTo: ['makeup', 'base', 'foundation']
        },
        {
          id: '10',
          hashtag: '#eyeshadow',
          posts: 3200000,
          engagement: 12500000,
          growth: '+14%',
          relatedTo: ['makeup', 'eyes', 'palette']
        },
        {
          id: '11',
          hashtag: '#lipstick',
          posts: 2800000,
          engagement: 11000000,
          growth: '+12%',
          relatedTo: ['makeup', 'lips', 'lipstick']
        },
        {
          id: '12',
          hashtag: '#makeupbrushes',
          posts: 1200000,
          engagement: 4800000,
          growth: '+18%',
          relatedTo: ['brushes', 'tools', 'makeup']
        },
        {
          id: '13',
          hashtag: '#waterproofmakeup',
          posts: 850000,
          engagement: 3200000,
          growth: '+25%',
          relatedTo: ['makeup', 'waterproof', 'longlasting']
        },
        {
          id: '14',
          hashtag: '#skincareroutine',
          posts: 1800000,
          engagement: 8500000,
          growth: '+12%',
          relatedTo: ['skincare', 'routine']
        },
        {
          id: '15',
          hashtag: '#makeuppalette',
          posts: 1500000,
          engagement: 6000000,
          growth: '+16%',
          relatedTo: ['palette', 'makeup', 'colors']
        }
      ])
      setPosts([
        {
          id: '1',
          image: 'https://via.placeholder.com/400x400?text=Guilty+Beauty+Post',
          caption: 'Novo lançamento! ✨ #guiltybeauty #beauty',
          likes: 1250,
          comments: 89,
          date: '2024-01-13',
          hashtags: ['#guiltybeauty', '#beauty', '#newproduct']
        }
      ])
      setLoading(false)
    }, 1000)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Instagram</h1>
          <p className="text-gray-600 mt-1">Trends e análise da marca</p>
          <a 
            href="https://www.instagram.com/guiltybeauty.oficial/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-sm text-gray-500 hover:text-black mt-2 inline-flex items-center gap-1"
          >
            @guiltybeauty.oficial <ExternalLink className="w-3 h-3" />
          </a>
        </div>
        <button
          onClick={fetchInstagramData}
          className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-900 transition"
        >
          <RefreshCw className="w-4 h-4" />
          Atualizar
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Seguidores</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">0</p>
            </div>
            <TrendingUp className="w-8 h-8 text-black" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Engajamento</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">0%</p>
            </div>
            <Heart className="w-8 h-8 text-black" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Posts</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">0</p>
            </div>
            <MessageCircle className="w-8 h-8 text-black" />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <Hash className="w-5 h-5" />
            Trends Relacionadas à Marca
          </h2>
        </div>
        <div className="p-6">
          <div className="space-y-4">
            {trends.map((trend) => (
              <div key={trend.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Hash className="w-4 h-4 text-black" />
                      <span className="font-semibold text-lg text-black">{trend.hashtag}</span>
                      <span className={`text-sm font-medium ${
                        trend.growth.startsWith('+') ? 'text-black' : 'text-gray-600'
                      }`}>
                        {trend.growth}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-3">
                      <div>
                        <p className="text-xs text-gray-500">Posts</p>
                        <p className="text-sm font-semibold text-gray-900">
                          {trend.posts.toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Engajamento</p>
                        <p className="text-sm font-semibold text-gray-900">
                          {trend.engagement.toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Relacionado</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {trend.relatedTo.map((tag, idx) => (
                            <span key={idx} className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded">
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Posts Recentes</h2>
        </div>
        <div className="p-6">
          {posts.length === 0 ? (
            <p className="text-gray-500 text-center py-8">Nenhum post encontrado</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {posts.map((post) => (
                <div key={post.id} className="border border-gray-200 rounded-lg overflow-hidden">
                  <img 
                    src={post.image} 
                    alt={post.caption}
                    className="w-full h-64 object-cover"
                  />
                  <div className="p-4">
                    <p className="text-sm text-gray-700 line-clamp-2 mb-3">{post.caption}</p>
                    <div className="flex items-center justify-between text-sm text-gray-500 mb-3">
                      <div className="flex items-center gap-4">
                        <span className="flex items-center gap-1">
                          <Heart className="w-4 h-4" />
                          {post.likes.toLocaleString()}
                        </span>
                        <span className="flex items-center gap-1">
                          <MessageCircle className="w-4 h-4" />
                          {post.comments}
                        </span>
                      </div>
                      <span>{post.date}</span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {post.hashtags.map((tag, idx) => (
                        <span key={idx} className="text-xs text-gray-600">{tag}</span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
